import { useFormik } from 'formik'
import { useState } from 'react'
import { NumericFormat } from 'react-number-format'
import { getCurrencySymbol, parseCurrencyToNumber } from '../../helpers'
import { FeeCharges } from '../../redux-toolkits/loyalty/loyalty.type'
import {
  useCreateFeeMutation,
  useUpdateFeeMutation,
} from '../../redux-toolkits/vendors/vendors.slice'
import { SingleVendorFee } from '../../redux-toolkits/vendors/vendors.types'
import { VendorServiceChargeSchema } from '../../validation/validate'
import Icon from '../icon'
import Dropdown from '../inputs/dropdown'
import { EditInput } from '../inputs/editInput'
import { TextInput } from '../inputs/textInput'

const feeOptions = [
  {
    name: 'Percentage',
    value: 'Percentage',
  },
  {
    name: 'Flat Fee',
    value: 'Flat',
  },
]

export const VendorServiceChargeManagement = ({
  fee,
  vendorId,
  className,
}: {
  fee?: SingleVendorFee
  vendorId?: string
  className?: string
}) => {
  const [editing, setEditing] = useState(false)

  const [createFee, { isLoading: creatingFee }] = useCreateFeeMutation()
  const [updateFee, { isLoading: updatingFee }] = useUpdateFeeMutation()

  const {
    values,
    setFieldValue,
    handleChange,
    handleBlur,
    handleSubmit,
    errors,
    touched,
  } = useFormik({
    initialValues: {
      typeName: fee
        ? feeOptions.find((f) => f.value === fee.feeCharge)?.name
        : '',
      type: fee ? fee.feeCharge : '',
      value: fee
        ? fee.feeCharge === 'Flat'
          ? fee.feeAmount
          : fee.feePercentage
        : '',
      minServiceCharge: fee ? fee.feeFloor : '',
      maxServiceCharge: fee ? fee.feeCeiling : '',
    },
    validationSchema: VendorServiceChargeSchema,
    onSubmit: async (vals) => {
      if (fee) {
        const resp = await updateFee({
          id: fee._id,
          payload: {
            feeOption: 'SERVICE_CHARGE',
            feeCharge: vals.type as FeeCharges,
            feePercentage:
              vals.type === 'Percentage'
                ? parseCurrencyToNumber(vals.value)
                : undefined,
            feeAmount:
              vals.type === 'Flat'
                ? parseCurrencyToNumber(vals.value)
                : undefined,
            feeFloor: parseCurrencyToNumber(vals.minServiceCharge),
            feeCeiling: parseCurrencyToNumber(vals.maxServiceCharge),
          },
        })

        if (resp.data?.success) {
          return
        }
      } else {
        const resp = await createFee({
          vendor: vendorId ?? '',
          feeOption: 'SERVICE_CHARGE',
          feeCharge: vals.type as FeeCharges,
          feePercentage:
            vals.type === 'Percentage'
              ? parseCurrencyToNumber(vals.value)
              : undefined,
          feeAmount:
            vals.type === 'Flat'
              ? parseCurrencyToNumber(vals.value)
              : undefined,
          feeFloor: parseCurrencyToNumber(vals.minServiceCharge),
          feeCeiling: parseCurrencyToNumber(vals.maxServiceCharge),
        })

        if (resp.data?.success) {
          return
        }
      }
    },
  })

  return (
    <div className={className}>
      <EditInput
        onEditSave={() => {
          handleSubmit()
        }}
        editing={editing}
        onEditClick={() => {
          setEditing(true)
        }}
        onCancel={() => {
          setEditing(false)
        }}
        title="Service Charge"
        loading={creatingFee || updatingFee}
      >
        {({ isEditing }) => {
          return isEditing ? (
            <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
              <Dropdown
                loading={false}
                className=""
                menuClassName="!max-h-[200px]"
                menuButton={
                  <TextInput
                    type="text"
                    name="type"
                    title="Charge type"
                    placeholder="Choose Charge Type"
                    value={values.typeName}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    errors={errors.type && touched.type ? errors.type : ''}
                    hasIcon={false}
                    readOnly
                    extra={
                      <Icon
                        id={'left-caret'}
                        width={16}
                        height={16}
                        className="-rotate-90 text-sec-black"
                      />
                    }
                  />
                }
                menuItems={feeOptions.map((opt) => ({
                  ...opt,
                  onClick: () => {
                    setFieldValue('type', opt.value)
                    setFieldValue('typeName', opt.name)
                  },
                }))}
              />

              <NumericFormat
                suffix={values.type === 'Percentage' ? '%' : undefined}
                prefix={
                  values.type === 'Percentage'
                    ? undefined
                    : getCurrencySymbol('NGN')
                }
                customInput={TextInput}
                type="text"
                name="value"
                title="Charge Value"
                placeholder="Enter Charge Value"
                value={values.value}
                onBlur={handleBlur}
                onChange={handleChange}
                errors={errors.value && touched.value ? errors.value : ''}
                hasIcon={false}
                decimalScale={2}
                thousandSeparator
              />

              <NumericFormat
                suffix={undefined}
                prefix={getCurrencySymbol('NGN')}
                customInput={TextInput}
                type="text"
                name="minServiceCharge"
                title="Minimum Service Charge"
                placeholder="Enter Minimum Service Charge"
                value={values.minServiceCharge}
                onBlur={handleBlur}
                onChange={handleChange}
                errors={
                  errors.minServiceCharge && touched.minServiceCharge
                    ? errors.minServiceCharge
                    : ''
                }
                hasIcon={false}
                decimalScale={2}
                thousandSeparator
              />

              <NumericFormat
                suffix={undefined}
                prefix={getCurrencySymbol('NGN')}
                customInput={TextInput}
                type="text"
                name="maxServiceCharge"
                title="Maximum Service Charge"
                placeholder="Maximum Service Charge"
                value={values.maxServiceCharge}
                onBlur={handleBlur}
                onChange={handleChange}
                errors={
                  errors.maxServiceCharge && touched.maxServiceCharge
                    ? errors.maxServiceCharge
                    : ''
                }
                hasIcon={false}
                decimalScale={2}
                thousandSeparator
              />
            </div>
          ) : (
            <div>
              {values.value
                ? `${values.value} • ${values?.type}`
                : 'Not Configured'}
            </div>
          )
        }}
      </EditInput>
    </div>
  )
}
